// Sprint chat-header-restore-name-drop-idle (2026-05-24) — partial
// reversal of chat-header-slim. The chat-header-slim sprint mis-read
// Santi's intent: he wanted the AGENT NAME kept and the StatusPill
// `Idle · Send a message…` dropped. The implementer did the inverse.
//
// Layout (left → right) — restored:
//   [☰ hamburger (mobile)]  [name (double-click rename)]
//   [status pill — only when running] [branch chip] [↑↓ ahead/behind]
//   [R1 runner chip] [DualMode chip] … [⋯ kebab DropdownMenu]
//
// Still removed (correct part of chat-header-slim):
// - server/host chip ("VPS-CONTROL fsn1") — R1 already identifies the runner.
// - project chip — sidebar groups by project; composer's ProjectChip is the
//   single in-chat surface for project confirmation.
//
// DS.5 (sprint aos-ds-shell, spec §2 D3/D4/D5): the conversation topbar on DS
// pieces — the ⋯ kebab is the DS `Menu` (`items[]`, through ds/DsMenu.tsx),
// the search toggle and the kebab are DS `IconButton`s (ds/DsIconButton),
// their tooltips the DS `Tooltip` (ds/DsTooltip: portal contained in
// `.aos-ds-root`), the rename field the DS `TextField` (C16: `ui/input`'s
// importer count 9 → 8), the glyphs `@ds/primitives/Icon`. The four chips
// stay product blocks (D2: approved-shell.png mounted THIS header as-is, so
// the bare chips are the approved look; the DS `Chip` pill is not).

import { useState } from 'react';
import type { Agent, EffortLevel } from '../types';
import { useAppStore } from '../stores/app.store';
import { useToastStore } from '../stores/toast.store';
import { patchAgentConfig, failoverAgentRequest } from '../lib/api';
import { RunnerPickerModal } from './RunnerPickerModal';
import { reconcileEffort } from '../lib/models';
import { ConfirmDialog } from './ConfirmDialog';
import { BranchChip } from './BranchChip';
import { AheadBehindChip } from './AheadBehindChip';
import { RunnerChip } from './RunnerChip';
import { DualModeHeaderChip } from './founder/DualModeChip';
import { StatusPill } from './atoms/StatusPill';
import { useInlineRename } from '../hooks/useInlineRename';
import { useMediaQuery, MD_QUERY } from '../hooks/useMediaQuery';
import { useTranslation } from '../i18n';
import TextField from '@ds/data-input/TextField';
import Icon from '@ds/primitives/Icon';
import { DsIconButton } from './ds/DsIconButton';
import { DsMenu } from './ds/DsMenu';
import { DsTooltip } from './ds/DsTooltip';

export interface ChatHeaderProps {
  agent: Agent;
  onStop: () => Promise<void>;
  onFork: () => Promise<void>;
  onRename: (name: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onOpenSidebar?: () => void;
  // Sprint fc-conversation-search (§2.2, §6.2 row 11) — magnifier trigger,
  // immediately left of the ⋯ menu. Optional so every existing test here
  // keeps passing unchanged; the row only renders when a handler is given.
  onToggleSearch?: () => void;
  // Gate 2 fix round item 9 — disabled while ChatView's own
  // `loading && messages === null` / `loadError && messages === null`
  // predicate holds, with that state's own reason as the tooltip/aria copy.
  searchDisabled?: boolean;
  searchDisabledReason?: string;
}

type Confirming = 'stop' | 'fork' | 'delete' | null;

export function ChatHeader(props: ChatHeaderProps) {
  const {
    agent,
    onStop,
    onFork,
    onRename,
    onDelete,
    onOpenSidebar,
    onToggleSearch,
    searchDisabled,
    searchDisabledReason,
  } = props;
  const { t } = useTranslation();
  const upsertAgent = useAppStore((s) => s.upsertAgent);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const isDesktop = useMediaQuery(MD_QUERY);

  const rename = useInlineRename({
    initial: agent.name,
    onCommit: async (next) => {
      await onRename(next);
    },
  });

  // Sprint 5.6 (B7) — chip config handler retained for future surfaces.
  async function applyConfig(next: { model?: string; effort?: EffortLevel }) {
    const optimistic: Agent = {
      ...agent,
      model: next.model ?? agent.model,
      effort: next.effort ?? agent.effort,
    };
    upsertAgent(optimistic);
    setConfigError(null);
    try {
      const updated = await patchAgentConfig(agent.id, next);
      upsertAgent(updated);
    } catch (err) {
      upsertAgent(agent);
      setConfigError(
        err instanceof Error ? err.message : 'Could not change model',
      );
    }
  }
  void applyConfig;
  void reconcileEffort;

  const [isStopping, setIsStopping] = useState(false);
  const [isForking, setIsForking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sprint move-runner-button (EC-135) — proactive "move to another
  // server" from the ⋯ menu: pick a destination (or Auto) → confirm
  // (memory travels, uncommitted work does not) → POST failover with the
  // explicit target. Self-contained here, like `applyConfig`, so no new
  // prop threading through ChatView.
  const [movePickerOpen, setMovePickerOpen] = useState(false);
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState<string | undefined>(undefined);
  const [isMoving, setIsMoving] = useState(false);

  async function runMove() {
    setIsMoving(true);
    try {
      const res = await failoverAgentRequest(agent.id, moveTarget);
      upsertAgent(res.agent);
      useToastStore.getState().push({
        message: t('moveRunner.successToast', { hostname: res.failover.toHostname }),
      });
      setMoveConfirmOpen(false);
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      useToastStore.getState().push({
        message: t('moveRunner.errorToast', { reason }),
      });
    } finally {
      setIsMoving(false);
    }
  }

  async function runStop() {
    setIsStopping(true);
    try {
      await onStop();
      setConfirming(null);
    } finally {
      setIsStopping(false);
    }
  }

  async function runFork() {
    setIsForking(true);
    try {
      await onFork();
      setConfirming(null);
    } finally {
      setIsForking(false);
    }
  }

  async function runDelete() {
    setIsDeleting(true);
    try {
      await onDelete();
      setConfirming(null);
    } finally {
      setIsDeleting(false);
    }
  }

  function startRename() {
    rename.begin();
  }

  const nameClickHandlers = isDesktop
    ? { onDoubleClick: () => rename.begin() }
    : { onClick: () => rename.begin() };

  return (
    <>
      <header
        className="flex items-center gap-2 px-2 md:px-4 h-14 border-b border-border bg-background shrink-0 pt-safe-top box-content"
        data-testid="chat-header"
      >
        {onOpenSidebar && <HamburgerButton onClick={onOpenSidebar} />}

        <div className="flex-1 min-w-0 flex items-center gap-2">
          {rename.editing ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void rename.commit();
              }}
              className="flex-1 min-w-0"
            >
              <TextField
                fieldSize="sm"
                autoFocus
                value={rename.value}
                onChange={(e) => rename.setValue(e.target.value)}
                onBlur={() => void rename.commit()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') rename.cancel();
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void rename.commit();
                  }
                }}
                disabled={rename.busy}
                aria-label={t('agentList.renameAgent')}
                data-testid="rename-input"
              />
            </form>
          ) : (
            <>
              <h1
                className="text-sm font-semibold text-foreground truncate cursor-text select-none"
                data-testid="chat-header-name"
                title={isDesktop ? t('chatHeader.renameTooltipDesktop') : t('chatHeader.renameTooltipMobile')}
                {...nameClickHandlers}
              >
                {agent.name}
              </h1>

              {/* Sprint chat-header-restore-name-drop-idle gated this on
                  `agent.working`, because the only thing the pill could
                  say was "Working" and everything else was noise. Sprint
                  job-tree-four-states removes the gate: the pill now
                  carries the founder state AND its detail line, so
                  `Needs you` and `Unconfirmed` — the two states he can act
                  on — stop being invisible on the surface he is looking
                  at. That surface is D6's entire argument for killing the
                  push; leaving it gated would have made the argument
                  false.

                  FENCE — `done` RENDERS NOTHING, and this comment used to
                  say the opposite. PR #330 (D2, 2026-05-24) DELETED the
                  "Idle · Send a message to continue" cue on Santi's
                  verbatim instruction; the old gate existed to SUPPRESS
                  that string, not to protect it. An earlier draft of this
                  sprint read the fence backwards, un-gated the pill and
                  handed `done` the cue again — and since `founderStatus`
                  is always present in production, it would have reappeared
                  on every finished conversation. The suppression now lives
                  in `StatusPill`'s `founderState === 'done'` branch
                  (returns `null`), pinned by the first case in
                  `StatusPill.four-states.test.tsx`. Do not reintroduce it
                  here.

                  Fallback for a payload that predates the sprint: the
                  original `working` gate. */}
              {(agent.founderStatus || agent.working) && (
                <StatusPill
                  status={agent.status}
                  working={agent.working}
                  founderState={agent.founderStatus?.state}
                  severity={agent.founderStatus?.severity}
                  detail={agent.founderStatus?.detail}
                />
              )}

              <div className="hidden sm:flex shrink-0" data-testid="chat-header-branch-slot">
                <BranchChip gitStatus={agent.gitStatus ?? null} />
              </div>

              <div className="hidden sm:flex shrink-0" data-testid="chat-header-aheadbehind-slot">
                <AheadBehindChip gitStatus={agent.gitStatus ?? null} />
              </div>

              {/* Sprint 20.3 PR-9 (D20 / UC-38) — runner indicator chip.
                  Sits between the git-status chips and the dual-mode chip
                  so the founder reads "what code · which runner" left-to-right. */}
              <div className="hidden sm:flex shrink-0" data-testid="chat-header-runner-slot">
                <RunnerChip runnerId={agent.runnerId} />
              </div>

              <DualModeHeaderChip />
            </>
          )}
        </div>

        {configError && (
          <p
            className="text-xs text-destructive mr-2"
            role="alert"
            data-testid="chat-header-config-error"
          >
            {configError}
          </p>
        )}

        <div className="flex items-center gap-1 shrink-0">
          {onToggleSearch && (
            <DsTooltip
              content={
                searchDisabled && searchDisabledReason
                  ? searchDisabledReason
                  : t('chatHeader.search')
              }
              position="bottom"
            >
              <DsIconButton
                variant="standard"
                size="sm"
                onClick={onToggleSearch}
                disabled={searchDisabled}
                aria-label={
                  searchDisabled && searchDisabledReason
                    ? searchDisabledReason
                    : t('chatHeader.search')
                }
                data-testid="chat-header-search-toggle"
                className="text-muted-foreground"
                style={{ minHeight: 'auto', minWidth: 'auto' }}
              >
                <Icon name="search" size={18} />
              </DsIconButton>
            </DsTooltip>
          )}

          <DsMenu
            align="right"
            data-testid="header-menu"
            trigger={
              <DsTooltip content={t('chatHeader.moreActions')} position="bottom">
                <DsIconButton
                  variant="standard"
                  size="sm"
                  aria-label={t('chatHeader.moreActions')}
                  data-testid="menu-button"
                  className="text-muted-foreground"
                  style={{ minHeight: 'auto', minWidth: 'auto' }}
                >
                  <Icon name="more_horiz" size={18} />
                </DsIconButton>
              </DsTooltip>
            }
            items={[
              {
                label: <span data-testid="menu-rename">{t('contextMenu.rename')}</span>,
                icon: <Icon name="edit" size={14} />,
                onClick: startRename,
              },
              {
                label: <span data-testid="menu-move-runner">{t('moveRunner.menuItem')}</span>,
                icon: <Icon name="swap_horiz" size={14} />,
                onClick: () => setMovePickerOpen(true),
              },
              { label: null, divider: true },
              {
                label: (
                  <span className="text-destructive" data-testid="menu-delete">
                    {t('contextMenu.delete')}
                  </span>
                ),
                icon: <Icon name="delete" size={14} />,
                destructive: true,
                onClick: () => setConfirming('delete'),
              },
            ]}
          />
        </div>

        <ConfirmDialog
          open={confirming === 'stop'}
          title={t('confirm.stopAgent.title')}
          body={t('confirm.stopAgent.body')}
          confirmLabel={t('confirm.stopAgent.confirm')}
          destructive
          loading={isStopping}
          onConfirm={runStop}
          onCancel={() => setConfirming(null)}
        />
        <ConfirmDialog
          open={confirming === 'fork'}
          title={t('confirm.forkAgent.title')}
          body={t('confirm.forkAgent.body')}
          confirmLabel={t('confirm.forkAgent.confirm')}
          loading={isForking}
          onConfirm={runFork}
          onCancel={() => setConfirming(null)}
        />
        <ConfirmDialog
          open={confirming === 'delete'}
          title={t('confirm.deleteAgent.title')}
          body={t('confirm.deleteAgent.body')}
          confirmLabel={t('confirm.deleteAgent.confirm')}
          destructive
          loading={isDeleting}
          onConfirm={runDelete}
          onCancel={() => setConfirming(null)}
        />

        {/* Sprint move-runner-button — proactive move: pick destination
            (current server excluded), then a non-destructive confirm that
            spells out the memory-travels / uncommitted-work-doesn't
            trade-off, then POST failover with the chosen runnerId. */}
        <RunnerPickerModal
          open={movePickerOpen}
          onOpenChange={setMovePickerOpen}
          excludeRunnerId={agent.runnerId}
          title={t('moveRunner.pickerTitle')}
          confirmLabel={t('moveRunner.pickerConfirm')}
          onConfirm={(runnerId) => {
            setMoveTarget(runnerId);
            setMoveConfirmOpen(true);
          }}
        />
        <ConfirmDialog
          open={moveConfirmOpen}
          title={t('moveRunner.confirmTitle')}
          body={t('moveRunner.confirmBody')}
          confirmLabel={t('moveRunner.confirmConfirm')}
          loading={isMoving}
          onConfirm={runMove}
          onCancel={() => setMoveConfirmOpen(false)}
        />
      </header>
    </>
  );
}

// Mobile only (the `md:hidden` wrapper: a display utility on the piece's own
// node would lose to its CSS-module `display: inline-flex`).
function HamburgerButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <span className="md:hidden">
      <DsIconButton
        variant="standard"
        size="md"
        onClick={() => {
          if (
            typeof document !== 'undefined' &&
            document.activeElement instanceof HTMLElement
          ) {
            document.activeElement.blur();
          }
          onClick();
        }}
        aria-label={t('chatHeader.backAriaLabel')}
        data-testid="hamburger-button"
        className="text-muted-foreground"
        style={{ minHeight: 'auto', minWidth: 'auto' }}
      >
        <Icon name="menu" size={18} />
      </DsIconButton>
    </span>
  );
}
